import { useCallback, useEffect, useMemo, useState } from "react";
import { actualizarDespacho } from "../../../services/backend/deliveryService";
import { getComunas, getCiudad } from "../../../services/backend/adminService";
import { resolveCoordinates } from "../../../services/geo/geocodeService";
import {
  initialDeliveryCoordinates,
  initialDeliveryAddressParts,
  normalizeAddressPart,
  normalizeCommuneName,
  buildDeliveryAddressString,
} from "../../../utils/delivery";

const DELIVERY_MAP_DEBOUNCE_MS = 1000;

const useDeliveryAddress = ({
  cotizacionId,
  buyerClientInfo = {},
  onShowToast,
  onRefreshCheckoutSummary,
}) => {
  const [deliveryAddressParts, setDeliveryAddressParts] = useState(initialDeliveryAddressParts);
  const [deliveryCoordinates, setDeliveryCoordinates] = useState(initialDeliveryCoordinates);
  const [isChangingDeliveryAddress, setIsChangingDeliveryAddress] = useState(false);
  const [deliverySaveLoading, setDeliverySaveLoading] = useState(false);
  const [deliveryGeoLoading, setDeliveryGeoLoading] = useState(false);
  const [deliveryGeoError, setDeliveryGeoError] = useState("");
  const [debouncedDeliveryMapQuery, setDebouncedDeliveryMapQuery] = useState("");

  const [deliveryComunas, setDeliveryComunas] = useState([]);
  const [deliveryComunasLoading, setDeliveryComunasLoading] = useState(false);
  const [deliveryComunasError, setDeliveryComunasError] = useState("");
  const [deliveryCommuneCode, setDeliveryCommuneCode] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [deliveryCityLoading, setDeliveryCityLoading] = useState(false);

  const findDeliveryCommuneByCode = useCallback((code) =>
    deliveryComunas.find((c) => String(c?.codigo || "").trim() === String(code || "").trim()) || null,
    [deliveryComunas]);

  const findDeliveryCommuneByName = useCallback((communeName) =>
    deliveryComunas.find((c) => normalizeCommuneName(c?.descripcion) === normalizeCommuneName(communeName)) || null,
    [deliveryComunas]);

  const deliveryCommuneOptions = useMemo(
    () => deliveryComunas.map((c) => ({ label: String(c?.descripcion || "").trim(), value: String(c?.codigo || "").trim() })),
    [deliveryComunas]
  );

  const buildDeliveryPayload = useCallback(() => ({
    communeCode: deliveryCommuneCode,
    address: normalizeAddressPart(deliveryAddressParts.address),
    reference: normalizeAddressPart(deliveryAddressParts.reference),
  }), [deliveryCommuneCode, deliveryAddressParts]);

  const defaultDeliveryAddress = useMemo(() => {
    const direccion = String(buyerClientInfo?.direccion || "").trim();
    const communeCode = String(buyerClientInfo?.comuna || "").trim();
    if (!direccion) return null;
    const commune = findDeliveryCommuneByCode(communeCode);
    return { address: direccion, commune: String(commune?.descripcion || "").trim(), communeCode };
  }, [buyerClientInfo, findDeliveryCommuneByCode]);

  const deliveryMapQuery = useMemo(
    () => encodeURIComponent(
      deliveryCoordinates.lat && deliveryCoordinates.lng
        ? `${deliveryCoordinates.lat},${deliveryCoordinates.lng}`
        : buildDeliveryAddressString(deliveryAddressParts)
    ),
    [deliveryAddressParts, deliveryCoordinates]
  );

  const hasSavedDeliveryAddress = Boolean(defaultDeliveryAddress);
  const areDeliveryInputsDisabled = deliverySaveLoading || (hasSavedDeliveryAddress && !isChangingDeliveryAddress);

  const applyDefaultDeliveryAddress = useCallback((savedAddress) => {
    if (!savedAddress) return;
    setDeliveryAddressParts({
      address: normalizeAddressPart(savedAddress.address),
      reference: "",
      commune: normalizeAddressPart(savedAddress.commune),
    });
    setDeliveryCommuneCode(String(savedAddress.communeCode || "").trim());
    setDeliveryCoordinates(initialDeliveryCoordinates);
    setDeliveryGeoError("");
    setIsChangingDeliveryAddress(false);
  }, []);

  const persistDeliveryAddress = useCallback(async (cambia, payload = {}) => {
    if (!cotizacionId) {
      setDeliveryGeoError("No se encontró una cotización activa para actualizar el despacho.");
      return;
    }
    setDeliverySaveLoading(true);
    setDeliveryGeoError("");
    try {
      await actualizarDespacho(
        cotizacionId, cambia,
        cambia ? (payload.communeCode || null) : null,
        cambia ? (payload.address || null) : null,
        cambia ? (payload.reference || null) : null
      );
      onRefreshCheckoutSummary?.();
    } catch (error) {
      const message = error?.message || "No fue posible actualizar la dirección de despacho.";
      setDeliveryGeoError(message);
      onShowToast?.({ severity: "error", summary: "Despacho", detail: message, life: 4000 });
    } finally {
      setDeliverySaveLoading(false);
    }
  }, [cotizacionId, onRefreshCheckoutSummary, onShowToast]);

  const handleChangeDeliveryAddressToggle = useCallback(async (checked) => {
    if (checked) {
      setIsChangingDeliveryAddress(true);
      await persistDeliveryAddress(true);
      return;
    }
    setIsChangingDeliveryAddress(false);
    applyDefaultDeliveryAddress(defaultDeliveryAddress);
    await persistDeliveryAddress(false);
  }, [applyDefaultDeliveryAddress, defaultDeliveryAddress, persistDeliveryAddress]);

  const handleResolveDeliveryCoordinates = useCallback(async () => {
    const normalizedAddress = buildDeliveryAddressString(deliveryAddressParts);
    if (!normalizedAddress) {
      setDeliveryCoordinates(initialDeliveryCoordinates);
      setDeliveryGeoError("");
      if (isChangingDeliveryAddress) {
        await persistDeliveryAddress(true, buildDeliveryPayload());
      }
      return;
    }
    setDeliveryGeoLoading(true);
    setDeliveryGeoError("");
    try {
      const result = await resolveCoordinates(normalizedAddress);
      setDeliveryCoordinates({ lat: result.lat, lng: result.lng });
      const nextParts = result.address;
      const matchedCommune = findDeliveryCommuneByName(nextParts.commune);
      setDeliveryAddressParts((prev) => ({ ...prev, commune: nextParts.commune || prev.commune }));
      if (matchedCommune?.codigo) setDeliveryCommuneCode(String(matchedCommune.codigo).trim());
    } catch (error) {
      setDeliveryCoordinates(initialDeliveryCoordinates);
      setDeliveryGeoError(error?.message || "No fue posible obtener las coordenadas.");
    } finally {
      setDeliveryGeoLoading(false);
      if (isChangingDeliveryAddress) {
        await persistDeliveryAddress(true, buildDeliveryPayload());
      }
    }
  }, [deliveryAddressParts, findDeliveryCommuneByName, isChangingDeliveryAddress, persistDeliveryAddress, buildDeliveryPayload]);

  const handleDeliveryAddressPartChange = useCallback((field, value) => {
    setDeliveryAddressParts((prev) => ({ ...prev, [field]: value }));
    setDeliveryCoordinates(initialDeliveryCoordinates);
    setDeliveryGeoError("");
  }, []);

  const handleDeliveryCommuneChange = useCallback((event) => {
    const nextCode = String(event.value || "").trim();
    const selectedCommune = findDeliveryCommuneByCode(nextCode);
    setDeliveryCommuneCode(nextCode);
    setDeliveryCity("");
    setDeliveryAddressParts((prev) => ({ ...prev, commune: String(selectedCommune?.descripcion || "").trim() }));
    setDeliveryCoordinates(initialDeliveryCoordinates);
    setDeliveryGeoError("");
  }, [findDeliveryCommuneByCode]);

  useEffect(() => {
    if (!deliveryMapQuery) { setDebouncedDeliveryMapQuery(""); return; }
    const id = window.setTimeout(() => setDebouncedDeliveryMapQuery(deliveryMapQuery), DELIVERY_MAP_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [deliveryMapQuery]);

  useEffect(() => {
    let isCurrent = true;
    setDeliveryComunasLoading(true);
    setDeliveryComunasError("");
    getComunas()
      .then((response) => { if (isCurrent) setDeliveryComunas(Array.isArray(response?.comunas) ? response.comunas : []); })
      .catch((error) => { if (isCurrent) { setDeliveryComunas([]); setDeliveryComunasError(error?.message || "Error al cargar comunas."); } })
      .finally(() => { if (isCurrent) setDeliveryComunasLoading(false); });
    return () => { isCurrent = false; };
  }, []);

  useEffect(() => {
    if (!deliveryCommuneCode) { setDeliveryCity(""); setDeliveryCityLoading(false); return; }
    let isCurrent = true;
    setDeliveryCityLoading(true);
    getCiudad(deliveryCommuneCode)
      .then((response) => { if (isCurrent) setDeliveryCity(String(response?.ciudad || "").trim()); })
      .catch(() => { if (isCurrent) setDeliveryCity(""); })
      .finally(() => { if (isCurrent) setDeliveryCityLoading(false); });
    return () => { isCurrent = false; };
  }, [deliveryCommuneCode]);

  // Sincroniza communeCode cuando llega un nombre de comuna sin código
  useEffect(() => {
    if (!deliveryAddressParts.commune) return;
    const matchedCommune = findDeliveryCommuneByName(deliveryAddressParts.commune);
    if (matchedCommune?.codigo) setDeliveryCommuneCode(String(matchedCommune.codigo).trim());
  }, [deliveryAddressParts.commune, findDeliveryCommuneByName]);

  // Aplica dirección por defecto al cargar buyerClientInfo
  useEffect(() => {
    if (!defaultDeliveryAddress || isChangingDeliveryAddress) return;
    if (deliveryAddressParts.address || deliveryAddressParts.commune) return;
    applyDefaultDeliveryAddress(defaultDeliveryAddress);
  }, [defaultDeliveryAddress, isChangingDeliveryAddress, deliveryAddressParts, applyDefaultDeliveryAddress]);

  return {
    deliveryAddressParts,
    isChangingDeliveryAddress,
    deliverySaveLoading,
    deliveryGeoLoading,
    deliveryGeoError,
    setDeliveryGeoError,
    debouncedDeliveryMapQuery,
    defaultDeliveryAddress,
    areDeliveryInputsDisabled,
    handleChangeDeliveryAddressToggle,
    handleResolveDeliveryCoordinates,
    handleDeliveryAddressPartChange,
    handleDeliveryCommuneChange,
    deliveryCommuneCode,
    deliveryCommuneOptions,
    deliveryComunasLoading,
    deliveryComunasError,
    deliveryCity,
    deliveryCityLoading,
  };
};

export default useDeliveryAddress;